import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { buildPriceRows, type PriceRow, type SealedSnapshot } from "./prices-shared";
import type { AutomationStatus, EbayRateLimit } from "./prices.server";

/**
 * Public price board. Reads stored daily snapshots and blends them with the
 * transparent Pal Stars Index baseline so every tracked card has a value.
 */
// Use POST so browsers and intermediary caches can never reuse an old GET RPC
// response after the daily sync has written a newer price board.
type PriceBoard = {
  rows: PriceRow[];
  sealed: SealedSnapshot[];
  updated: string;
  stale?: boolean;
  liveSources: string[];
};

// Warm-worker cache. Reading + blending every snapshot cost ~3s of TTFB on the
// price pages; the board only changes when the daily sync writes, so a five
// minute in-memory reuse keeps the data current and the render fast.
let boardCache: { syncAt: string | null; board: PriceBoard } | null = null;

async function loadPriceBoard(): Promise<PriceBoard> {
  const { readSnapshots, readSealedSnapshots, lastSyncAt } = await import("./prices.server");
  const sync = await lastSyncAt();
  const syncAt = sync.at ?? null;
  if (boardCache && boardCache.syncAt === syncAt) return boardCache.board;

  const [snapshots, sealed] = await Promise.all([readSnapshots(), readSealedSnapshots()]);
  const board: PriceBoard = {
    rows: buildPriceRows(snapshots),
    sealed: sealed as unknown as SealedSnapshot[],
    updated: syncAt ?? new Date().toISOString(),
    stale: sync.stale,
    liveSources: [...new Set(snapshots.map((s) => s.source))].filter((s) => s !== "palstars-index"),
  };
  boardCache = { syncAt, board };
  return board;
}

export const getPriceBoard = createServerFn({ method: "POST" }).handler(async (): Promise<PriceBoard> => {
  try {
    return await loadPriceBoard();
  } catch (e) {
    console.error("getPriceBoard failed", e);
    return { rows: buildPriceRows([]), sealed: [], updated: new Date().toISOString(), liveSources: [] };
  }
});


/**
 * Just the real last-sync timestamp. Lightweight companion to getPriceBoard so
 * affiliate disclosures on non-price pages can show the same synced stamp
 * without pulling the whole price board.
 */
export const getPriceSyncTime = createServerFn({ method: "POST" }).handler(async (): Promise<{ updated: string | null }> => {
  try {
    const { lastSyncAt } = await import("./prices.server");
    const sync = await lastSyncAt();
    return { updated: sync.at ?? null };
  } catch {
    return { updated: null };
  }
});

/**
 * Server-rendered market snippet for ONE printed card, used in the card page's
 * meta description so the SERP result carries the live number, the real
 * last-sync time and a one-line reason for the latest move.
 *
 * Cached in-worker for ten minutes so per-card page renders do not each re-read
 * the whole snapshot table.
 */
export const getCardMarketMeta = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => {
    const code = typeof (data as { code?: unknown })?.code === "string" ? String((data as { code: string }).code) : "";
    if (!code) throw new Error("code required");
    return { code: code.toUpperCase() };
  })
  .handler(async ({ data }): Promise<{
    market: number | null;
    change7d: number | null;
    graded: number | null;
    updated: string | null;
    note: string | null;
  }> => {
    try {
      const board = await loadPriceBoard();
      const { movementNote } = await import("./index-movement");
      const key = data.code;
      const row =
        board.rows.find((r) => r.code.toUpperCase() === key) ??
        board.rows.find((r) => r.base.toUpperCase() === key) ??
        null;
      return {
        market: row && row.market > 0 ? row.market : null,
        change7d: row ? row.change7d : null,
        graded: row?.gradedAvg ?? null,
        updated: board.updated,
        note: movementNote(board.rows)?.text ?? null,
      };
    } catch (e) {
      console.error("getCardMarketMeta failed", e);
      return { market: null, change7d: null, graded: null, updated: null, note: null };
    }
  });

/**
 * Live eBay daily call allowances (Developer Analytics getRateLimits). Admin
 * only: it reports how much headroom the sync has, which is operational detail.
 */
export const getEbayRateLimits = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ limits: EbayRateLimit[]; budget: number; error: string }> => {
    const { data: isAdmin } = await context.supabase.rpc("current_user_has_role", { _role: "admin" });
    if (isAdmin !== true) throw new Error("Forbidden — admins only.");
    const { readRateLimits } = await import("./prices.server");
    return await readRateLimits();
  });

/**
 * Read-only automation health: whether the daily sync ran, how many rows each
 * feed wrote, and whether a completed-sales credential is configured. Only
 * booleans about credentials are returned — never a key value.
 *
 * ADMINS ONLY. Feed coverage, rejection counts and run cadence describe how the
 * Pal Stars price board is built, which is competitive information and of no use
 * to a visitor, so the data is not readable without the admin role.
 */
export const getPriceAutomation = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AutomationStatus> => {
    const { data: isAdmin } = await context.supabase.rpc("current_user_has_role", { _role: "admin" });
    if (isAdmin !== true) throw new Error("Forbidden — admins only.");
    const { readAutomationStatus } = await import("./prices.server");
    return await readAutomationStatus();
  });

